import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { SHADOWS } from '../../../../styles/UISystem';
import { blackPill, whitePill } from '../../../UISystem/sharePillStyles';
import { fitTextSize } from '../../../../utils/fitText';

/**
 * HostThreadVenueSheet — "Where you met", THREAD's FIRST host sheet
 * (variants block plan 3, Step 9; design `profile_1_where.png`).
 *
 * This is the one real host-side build. Today venue lives as a PILL INSIDE the
 * share sheet (`venueLine` + `onVenueEdit`), which is what makes that sheet
 * read crowded: greeting field, venue pill, and channel pills all competing in
 * one surface. `profile_1` splits it — venue gets its own sheet that raises
 * first, and the share sheet is then only about sharing.
 *
 * Three stacked rows, and the shape is better than what it replaces:
 *
 *   BLACK   the venue guess ("Hoxton Hotel") — the likely answer, one tap
 *   WHITE   the neighborhood ("Williamsburg") — now a VISIBLE equal option
 *           rather than something hiding behind a Skip
 *   WHITE   "Venue or event" — free entry, opens the existing picker
 *
 * It writes nothing itself: the caller keeps using `confirmVenue` /
 * `changeVenue` and the existing LocationOverlay, so the venue record, the
 * peer sync, and the per-side meeting rows are all untouched rails.
 *
 * Rows degrade individually — a GPS-denied room simply has no venue guess and
 * no neighborhood, leaving free entry, which is still a complete answer.
 */
const HostThreadVenueSheet = ({
  venueName,
  neighborhood,
  onPickVenue,
  onPickNeighborhood,
  onFreeEntry,
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Rows are the app's standard shadowed share pill (UISystem/sharePillStyles)
  // — a shadow, not a border, is what makes them read as controls.
  //
  // Venue names are unbounded ("Carmela Coffee - West Village"), so the label
  // shrinks to stay on one line rather than wrapping and breaking the row's
  // height. Shrink beats truncate for a NAME — the tail is what tells you
  // which place it is.

  return (
    <motion.div
      initial={{ y: 400 }}
      animate={{ y: 0 }}
      exit={{ y: 400 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        backgroundColor: 'white',
        borderTopLeftRadius: '24px',
        borderTopRightRadius: '24px',
        boxShadow: SHADOWS.TIER3,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          padding: '28px 24px',
          paddingBottom: 'calc(28px + env(safe-area-inset-bottom))',
          maxWidth: '340px',
          margin: '0 auto',
        }}
      >
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#000000' }}>Where you met</div>

        {venueName && (
          <div
            onClick={onPickVenue}
            style={{ ...blackPill(), fontSize: `${fitTextSize(venueName)}px` }}
          >
            {venueName}
          </div>
        )}

        {neighborhood && (
          <div
            onClick={onPickNeighborhood}
            style={{ ...whitePill(), fontSize: `${fitTextSize(neighborhood)}px` }}
          >
            {neighborhood}
          </div>
        )}

        <div onClick={onFreeEntry} style={whitePill()}>
          Venue or event
        </div>
      </div>
    </motion.div>
  );
};

HostThreadVenueSheet.propTypes = {
  venueName: PropTypes.string,
  neighborhood: PropTypes.string,
  onPickVenue: PropTypes.func.isRequired,
  onPickNeighborhood: PropTypes.func.isRequired,
  onFreeEntry: PropTypes.func.isRequired,
};

export default HostThreadVenueSheet;
